import { formatDuration, formatTimestamp } from '@shared/utils';
import formatWorkflowRunStartedAt from '@/lib/workflows/formatWorkflowRunStartedAt.js';

const SOURCE_LABELS = { manual: 'Run manually', schedule: 'Scheduled', webhook: 'Webhook', trigger: 'Trigger' };

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/* The run's facts for the run logs header, as one quiet line: when it started, how long it ran, what started it, and what
   it cost. Read from the run record, the same row the run lists show, so the header never disagrees with them — and a
   duration exists only when both ends of the run do, rather than a "0 ms" standing in for "not known". A person's test
   says so, because which runs were tests is what separates "verified" from "it ran". Each fact carries `title` when there
   is more to it than fits the line. */
const buildWorkflowRunFacts = ({ run, truncatedPayloadCount = 0 } = {}) => {
  if (!run) {
    return [];
  }

  const startedAtMs = Date.parse(run.startedAt || run.createdAt || '');
  const finishedAtMs = Date.parse(run.finishedAt || '');
  const facts = [];

  if (Number.isFinite(startedAtMs)) {
    const finishedTitle = Number.isFinite(finishedAtMs) ? ` · finished ${formatTimestamp(run.finishedAt)}` : '';
    facts.push({ key: 'started', label: formatWorkflowRunStartedAt(startedAtMs), title: `Started ${formatTimestamp(run.startedAt || run.createdAt)}${finishedTitle}` });
  }

  if (Number.isFinite(startedAtMs) && Number.isFinite(finishedAtMs) && finishedAtMs >= startedAtMs) {
    facts.push({ key: 'duration', label: `Ran ${formatDuration(Math.max(1, Math.round((finishedAtMs - startedAtMs) / 1000)))}` });
  }

  // A resent webhook call says so, and which run it repeats, so it is never mistaken for a second call from outside.
  const resentFromRunId = run.meta?.runtime?.replayOfRunId;
  const sourceLabel = run.meta?.test ? 'Test' : resentFromRunId ? 'Webhook resent' : SOURCE_LABELS[run.sourceType] || run.sourceType;

  if (sourceLabel) {
    facts.push({ key: 'source', label: sourceLabel, title: resentFromRunId ? `Resent from run ${resentFromRunId}` : undefined });
  }

  if (run.taskCount > 0) {
    facts.push({ key: 'billed', label: plural(run.taskCount, 'billed step') });
  }

  if (run.aiUsageCost > 0) {
    facts.push({ key: 'ai', label: `$${Number(run.aiUsageCost).toFixed(4)} AI usage` });
  }

  if (truncatedPayloadCount > 0) {
    facts.push({ key: 'truncated', label: `${plural(truncatedPayloadCount, 'payload')} shortened`, title: 'Shortened for storage; the step output says which.' });
  }

  return facts;
};

export default buildWorkflowRunFacts;
